import { Axios } from './axios'
import { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import ReconnectingWebSocket from 'reconnecting-websocket'
import { INSTANCE_ID, LOCALE } from './storageConsts'

const KEEP_ALIVE_MESSAGE = 'KEEP_ALIVE'
const FIRST_KEEP_ALIVE_DELAY = 60000 // 1 minute for first message
const KEEP_ALIVE_REPEAT_INTERVAL = 60000 // 1 minute between subsequent messages
const INSTANCE_UNAVAILABLE_FALLBACK_EVENT = 'pyplan-instance-unavailable-fallback'
const ALLOWED_API_LANGUAGES = ['es-AR', 'pt-BR', 'en-US'] as const

let hasEmittedInstanceUnavailableFallback = false
let currentApiLanguage: string = 'en-US'

const normalizeApiLanguage = (language?: string | null): string => {
  if (!language) {
    return 'en-US'
  }

  const normalized = language.trim().replace('_', '-').toLowerCase()
  if (normalized.startsWith('es')) return 'es-AR'
  if (normalized.startsWith('pt')) return 'pt-BR'
  if (normalized.startsWith('en')) return 'en-US'
  return 'en-US'
}

const resolveInitialApiLanguage = (): string => {
  const locale = localStorage.getItem(LOCALE)
  if (locale && ALLOWED_API_LANGUAGES.includes(locale as typeof ALLOWED_API_LANGUAGES[number])) {
    return locale
  }
  return normalizeApiLanguage(navigator.language)
}

currentApiLanguage = resolveInitialApiLanguage()

export const setApiLanguage = (language?: string | null) => {
  currentApiLanguage = normalizeApiLanguage(language)
}

declare global {
  interface Window {
    ppWebSocket?: ReconnectingWebSocket
  }
}

/**
 * @class Api Class is a fancy es6 wrapper class for axios.
 *
 * @param {import("axios").AxiosRequestConfig} config - axios Request Config.
 * @link [AxiosRequestConfig](https://github.com/axios/axios#request-config)
 */
export class Api extends Axios {
  private keepAliveInterval: ReturnType<typeof setInterval> | null = null
  private keepAliveTimeout: ReturnType<typeof setTimeout> | null = null
  private activeRequests: number = 0

  /**
   * Initiates the KEEP_ALIVE messaging system for long-running requests.
   * - Increments the active requests counter
   * - For the first active request, sets up a timer that will:
   *   1. Wait for 1 minute before sending first KEEP_ALIVE
   *   2. Then send KEEP_ALIVE messages every minute while requests are active
   * - This prevents session purges during long operations
   */
  private startKeepAlive() {
    this.activeRequests++

    // Only set up the timer if this is the first active request
    if (this.activeRequests === 1) {
      // First KEEP_ALIVE after 1 minute
      this.keepAliveTimeout = setTimeout(() => {
        if (this.activeRequests > 0) {
          if (window.ppWebSocket?.readyState === WebSocket.OPEN) {
            window.ppWebSocket.send(KEEP_ALIVE_MESSAGE) // Send first message immediately after timeout
          }
          // Then start interval for subsequent messages
          this.keepAliveInterval = setInterval(() => {
            if (window.ppWebSocket?.readyState === WebSocket.OPEN) {
              window.ppWebSocket.send(KEEP_ALIVE_MESSAGE)
            }
          }, KEEP_ALIVE_REPEAT_INTERVAL)
        }
      }, FIRST_KEEP_ALIVE_DELAY)
    }
  }

  /**
   * Manages the cleanup of KEEP_ALIVE messaging system.
   * - Decrements the active requests counter
   * - When all requests are finished (counter reaches 0):
   *   1. Clears the initial timeout if it hasn't triggered yet
   *   2. Stops the interval that sends periodic KEEP_ALIVE messages
   * - Ensures no lingering timers when they're no longer needed
   */
  private stopKeepAlive() {
    this.activeRequests = Math.max(0, this.activeRequests - 1)

    // Only clean up timers if there are no more active requests
    if (this.activeRequests === 0) {
      if (this.keepAliveTimeout) {
        clearTimeout(this.keepAliveTimeout)
        this.keepAliveTimeout = null
      }
      if (this.keepAliveInterval) {
        clearInterval(this.keepAliveInterval)
        this.keepAliveInterval = null
      }
    }
  }

  /**
   * Creates an instance of api.
   * @param {import("axios").AxiosRequestConfig} conf
   */
  public constructor(conf: AxiosRequestConfig) {
    super(conf)

    this.interceptors.request.use(
      (param) => {
        this.startKeepAlive() // Start sending KEEP_ALIVE when request starts

        if (param?.url?.includes('/set-csrf/') || param?.url?.includes('/login/')) {
          return {
            ...param,
            headers: {
              ...param.headers,
              'Accept-Language': currentApiLanguage
            }
          }
        }
        const thereIsToken = sessionStorage.getItem('authToken') !== null
        if (thereIsToken) {
          if (param?.url?.includes('/interfaceExternalLink/validatePassword/')) {
            sessionStorage.removeItem('authToken')
            sessionStorage.removeItem(INSTANCE_ID)
            return param
          }
          return {
            ...param,
            headers: {
              ...param.headers,
              'Accept-Language': currentApiLanguage,
              ...this.getInstanceId(),
              ...this.getAuthToken()
            }
          }
        }
        return {
          ...param,
          headers: {
            ...param.headers,
            'Accept-Language': currentApiLanguage,
            ...this.getInstanceId()
          }
        }
      },
      (error: AxiosError) => {
        this.stopKeepAlive() // Stop on error
        // handling error
        throw error
      }
    )

    // this middleware is been called right before the response is get it by the method that triggers the request
    this.interceptors.response.use(
      (value) => {
        this.stopKeepAlive() // Stop when response is received
        return value
      },
      (error) => {
        this.stopKeepAlive() // Stop on error

        if (error?.response?.status === 428 && !hasEmittedInstanceUnavailableFallback) {
          // 428 Precondition Required is used here to indicate that the instance is unavailable (e.g., due to session purge)
          hasEmittedInstanceUnavailableFallback = true
          sessionStorage.removeItem(INSTANCE_ID)
          window.dispatchEvent(new CustomEvent(INSTANCE_UNAVAILABLE_FALLBACK_EVENT))
        }

        // support for error when responseType is of type blob
        // https://github.com/axios/axios/issues/815#issuecomment-453963910
        if (
          error.request?.responseType === 'blob' &&
          error.response?.data instanceof Blob &&
          error.response.data.type &&
          error.response.data.type.toLowerCase().indexOf('json') !== -1
        ) {
          return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
              if (error.response) {
                error.response.data = JSON.parse(String(reader.result))
              }
              resolve(Promise.reject(error))
            }
            reader.onerror = () => {
              reject(error)
            }
            reader.readAsText(error.response.data)
          })
        }
        return Promise.reject(error)
      }
    )
  }
  /**
   * Gets Instance ID.
   *
   * @returns {object | null} instanceId.
   * @memberof Api
   */
  public getInstanceId(): { 'instance-id': string } | null {
    const ssId = sessionStorage.getItem(INSTANCE_ID)
    return ssId ? { 'instance-id': ssId } : null
  }

  /**
   * Get token from session storage.
   *
   * @returns {string | null} authToken.
   * @memberof Api
   */
  public getAuthToken(): { Authorization: string } | null {
    const authToken = sessionStorage.getItem('authToken')
    return authToken ? { Authorization: `Token ${authToken}` } : null
  }

  /**
   * Get Uri
   *
   * @param {import("axios").AxiosRequestConfig} [config]
   * @returns {string}
   * @memberof Api
   */
  public getUri(config?: AxiosRequestConfig): string {
    return this.getUri(config)
  }
  /**
   * Generic request.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP axios response payload.
   * @memberof Api
   *
   * @example
   * api.request({
   *   method: "GET|POST|DELETE|PUT|PATCH"
   *   baseUrl: "http://www.domain.com",
   *   url: "/api/v1/users",
   *   headers: {
   *     "Content-Type": "application/json"
   *  }
   * }).then((response: AxiosResponse<User>) => response.data)
   *
   */
  public request<T, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
    return this.request(config)
  }
  /**
   * HTTP GET method, used to fetch data `statusCode`: 200.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} HTTP `axios` response payload.
   * @memberof Api
   */
  public get<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.get(url, config)
  }
  /**
   * HTTP OPTIONS method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} HTTP `axios` response payload.
   * @memberof Api
   */
  public options<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.options(url, config)
  }
  /**
   * HTTP DELETE method, `statusCode`: 204 No Content.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public delete<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.delete(url, config)
  }
  /**
   * HTTP HEAD method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public head<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.head(url, config)
  }
  /**
   * HTTP POST method `statusCode`: 201 Created.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template B - `BODY`: body request object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {B} data - payload to be send as the `request body`,
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public post<T, B, R = AxiosResponse<T>>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.post(url, data, config)
  }
  /**
   * HTTP PUT method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template B - `BODY`: body request object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {B} data - payload to be send as the `request body`,
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public put<T, B, R = AxiosResponse<T>>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.put(url, data, config)
  }
  /**
   * HTTP PATCH method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template B - `BODY`: body request object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {B} data - payload to be send as the `request body`,
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public patch<T, B, R = AxiosResponse<T>>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.patch(url, data, config)
  }
  /**
   *
   * @template T - type.
   * @param {import("axios").AxiosResponse<T>} response - axios response.
   * @returns {T} - expected object.
   * @memberof Api
   */
  public success<T>(response: AxiosResponse<T>): T {
    return response.data
  }
  /**
   *
   *
   * @template T type.
   * @param {AxiosError<T>} error
   * @memberof Api
   */
  public error<T>(error: AxiosError<T>): void {
    throw error
  }
}
